import { prefillValues, type ContractContext, type CustomVariableDef } from "@talentkontor/domain";
import { defaultValues } from "./anlegen";

/**
 * Entwurf auf eine neuere Fassung seiner Vorlage heben (K-520, Abgleich mit dem Altportal, 20.10.2026). Neuere Fassung heißt:
 * dieselbe Vorlage (Schlüssel und Sprache) der Agentur, aktiv, mit höherer Versionsnummer als der Entwurf. Beim Heben gelten Text,
 * vertraulicher Teil und eigene Angaben der neuen Fassung; schon eingetragene Werte bleiben, wo es die Angabe mit demselben Typ
 * weiter gibt, neue Angaben bekommen Standardwert und Vorbelegung aus den Stammdaten wie beim Anlegen.
 */
export interface FassungZeile {
  id: string;
  key: string;
  language: string;
  version: number;
  status: string;
  name: string;
}

export function neuereFassung<T extends FassungZeile>(vorlage: { key: string; language: string } | null, entwurfVersion: number | null, liste: readonly T[]): T | null {
  if (!vorlage || entwurfVersion == null) return null;
  return liste.filter((x) => x.key === vorlage.key && x.language === vorlage.language && x.status === "active" && x.version > entwurfVersion)
    .sort((a, b) => b.version - a.version)[0] ?? null;
}

const leer = (v: unknown) => v === undefined || v === null || (typeof v === "string" && v.trim() === "");

export function werteFuerFassung(neu: readonly CustomVariableDef[], alt: readonly CustomVariableDef[], werte: Record<string, unknown>, ctx: ContractContext): Record<string, unknown> {
  const out = defaultValues(neu);
  for (const d of neu) {
    const vorher = alt.find((x) => x.key === d.key);
    if (vorher && vorher.type === d.type && !leer(werte[d.key])) out[d.key] = werte[d.key];
  }
  return prefillValues(neu, out, ctx).values;
}

/** Welche eigenen Angaben kommen hinzu, welche entfallen (Bezeichnung in der Vertragssprache) */
export function angabenAenderung(neu: readonly CustomVariableDef[], alt: readonly CustomVariableDef[], lang: "de" | "en"): { neu: string[]; weg: string[] } {
  const name = (d: CustomVariableDef) => d.label[lang] || d.label.de || d.key;
  return {
    neu: neu.filter((d) => !alt.some((x) => x.key === d.key)).map(name),
    weg: alt.filter((d) => !neu.some((x) => x.key === d.key)).map(name),
  };
}

const ENTITAETEN: Record<string, string> = { "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": "\"", "&#39;": "'", "&nbsp;": " ", "&shy;": "" };

/** Gerenderter Vertrag als Absätze reinen Texts (Überschriften, Absätze, Listenpunkte, Tabellenzeilen) */
export function absaetze(html: string | null | undefined): string[] {
  return (html ?? "")
    .replace(/<(br)\s*\/?>/gi, " ")
    .replace(/<\/(p|h[1-6]|li|tr|div|blockquote|section)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&(amp|lt|gt|quot|#39|nbsp|shy);/g, (m) => ENTITAETEN[m] ?? m)
    .split("\n")
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

export type VergleichZeile = { art: "gleich" | "plus" | "minus"; text: string };

/** Absatzvergleich (längste gemeinsame Folge): unverändert, entfallen, neu, in der Reihenfolge des Textes */
export function absatzVergleich(vorher: readonly string[], nachher: readonly string[]): VergleichZeile[] {
  const n = vorher.length, m = nachher.length;
  const l: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) l[i]![j] = vorher[i] === nachher[j] ? l[i + 1]![j + 1]! + 1 : Math.max(l[i + 1]![j]!, l[i]![j + 1]!);
  const out: VergleichZeile[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (vorher[i] === nachher[j]) { out.push({ art: "gleich", text: vorher[i]! }); i++; j++; }
    else if (l[i + 1]![j]! >= l[i]![j + 1]!) out.push({ art: "minus", text: vorher[i++]! });
    else out.push({ art: "plus", text: nachher[j++]! });
  }
  while (i < n) out.push({ art: "minus", text: vorher[i++]! });
  while (j < m) out.push({ art: "plus", text: nachher[j++]! });
  return out;
}

/** Lange unveränderte Strecken einklappen: je Rand ein Absatz Zusammenhang, dazwischen „… n Absätze unverändert“ */
export function verdichten(zeilen: readonly VergleichZeile[], rand = 1): ({ art: "gleich" | "plus" | "minus"; text: string } | { art: "ausgelassen"; anzahl: number })[] {
  const out: ({ art: "gleich" | "plus" | "minus"; text: string } | { art: "ausgelassen"; anzahl: number })[] = [];
  let k = 0;
  while (k < zeilen.length) {
    if (zeilen[k]!.art !== "gleich") { out.push(zeilen[k]!); k++; continue; }
    let e = k;
    while (e < zeilen.length && zeilen[e]!.art === "gleich") e++;
    const strecke = zeilen.slice(k, e);
    const vorn = k === 0 ? 0 : rand, hinten = e === zeilen.length ? 0 : rand;
    if (strecke.length <= vorn + hinten + 1) out.push(...strecke);
    else {
      out.push(...strecke.slice(0, vorn));
      out.push({ art: "ausgelassen", anzahl: strecke.length - vorn - hinten });
      out.push(...strecke.slice(strecke.length - hinten));
    }
    k = e;
  }
  return out;
}
